import { z } from 'zod';

import {
  AtividadeCasaEnum,
  AutonomiaEnum,
  BoletimEnum,
  ChegadaEnum,
  ComportamentoEnum,
  DesempenhoEnum,
  FocoEnum,
} from './enums';
import type {
  AtividadeCasa,
  Autonomia,
  Boletim,
  Chegada,
  Comportamento,
  Desempenho,
  Foco,
  HorarioDoDiaType,
} from './enums';

/** `data` como `Date`, mesmo raciocínio de `alunos.dto.ts`. */

export type RegistroResumoOutput = {
  id: string;
  horarioId: string;
  matriculaId: string;
  alunoId: string;
  alunoNome: string;
  alunoWhatsapp: string | null;
  professorId: string;
  materiaId: string;
  data: Date;
  horarioPrevisto: HorarioDoDiaType;
  chegada: Chegada | null;
  boletim: Boletim | null;
  atividadeCasa: AtividadeCasa | null;
  foco: Foco | null;
  autonomia: Autonomia | null;
  comportamento: Comportamento | null;
  desempenho: Desempenho | null;
  // Exposto aqui (não só no Detalhe) pro card da lista, sem 2ª chamada.
  estagio: string | null;
  // `null` pra linha virtual; data da última escrita (`@updatedAt`).
  atualizadoEm: Date | null;
  relatorioTexto: string | null;
  relatorioEnviadoEm: Date | null;
  // `null` até o 1º "Melhorar com IA"; nunca volta a `null` (é por registro, não por texto).
  melhoradoEm: Date | null;
};

export type RegistroDetalheOutput = RegistroResumoOutput & {
  conteudoIds: string[];
  anotacao: string | null;
};

/** Mesmo formato na criação (`POST`) e em cada auto-save (`PUT`), sem checagem de coerência entre campos: a UI decide o que mostrar. */
export const RegistroInput = z.object({
  horarioId: z.uuid(),
  data: z.coerce.date(),
  chegada: ChegadaEnum.optional(),
  boletim: BoletimEnum.optional(),
  atividadeCasa: AtividadeCasaEnum.optional(),
  foco: FocoEnum.optional(),
  autonomia: AutonomiaEnum.optional(),
  comportamento: ComportamentoEnum.optional(),
  desempenho: DesempenhoEnum.optional(),
  conteudoIds: z.array(z.uuid()).optional(),
  anotacao: z.string().optional(),
  relatorioTexto: z.string().trim().max(4000).nullable().optional(),
});

export type MarcarRelatorioEnviadoOutput = { relatorioEnviadoEm: Date };

// PUT reaproveita o shape, sem `horarioId`/`data`. `regenerarRelatorio` (só aqui) pede pro backend recompor o texto-base com os campos salvos nesta chamada.
export const RegistroUpdateInput = RegistroInput.omit({ horarioId: true, data: true })
  .partial()
  .extend({ regenerarRelatorio: z.boolean().optional() });

export const ListarRegistrosQuery = z.object({
  data: z.coerce.date(),
});

/** Janela de `GET /registros/pendencias`, compartilhada entre `contarPendencias` e o aviso de `acompanhamento.page.tsx`. */
export const JANELA_PENDENCIAS_DIAS = 14;

/** `datas` (únicas, ordenadas) alimenta o seletor "ver dia"; pode ter menos entradas que `total`. */
export type PendenciasOutput = {
  total: number;
  datas: Date[];
};

export type RegistroResumoOutputType = RegistroResumoOutput;
export type RegistroDetalheOutputType = RegistroDetalheOutput;
export type RegistroInputType = z.infer<typeof RegistroInput>;
export type RegistroUpdateInputType = z.infer<typeof RegistroUpdateInput>;
export type ListarRegistrosQueryType = z.infer<typeof ListarRegistrosQuery>;
export type PendenciasOutputType = PendenciasOutput;
export type MarcarRelatorioEnviadoOutputType = MarcarRelatorioEnviadoOutput;
